/**
 * Data for /caseStudies, the all-case-studies listing page. Whether a
 * story or product actually HAS a page is never stored here — the page
 * component queries allSitePage and checks each `path` against it at
 * render time, so a page added later gains its call to action with no
 * data-file or component change (see src/pages/caseStudies/index.js).
 *
 * Metric/caption/photo values for the five stories that already had a
 * page when this list was written (Big Jerry's Fencing, Nurtureful
 * Psychology, Muscat Media Group, Orangeline Pakistan, EME Aquatic
 * Systems) are each copied from that page's own first result card and
 * intro photo, not invented here — several differ from a shorter/rounder
 * version that was briefed; see the build report for exactly what changed
 * and why EME shows a metric here despite being briefed as a "built line"
 * story (its own page's first result is a metric, not a built line).
 *
 * `clientLogoName` is the exact `name` of that client in
 * src/data/clientLogos.js — the actual file is resolved from there (always
 * the colour version on this page, never grey — see AllCaseStudiesPage.js
 * and the build report for which of these have no colour file yet and so
 * render with no logo chip at all). `null` where there's no logo file at
 * all (TTI Testing Laboratories). It isn't always the same string as this
 * story's own `name` above — Hush Puppies Pakistan's client is "Hush
 * Puppies" and Orangeline Pakistan's is "Orange Line" in that file.
 */

const STORIES = [
  {
    name: "Big Jerry's Fencing",
    industry: "Field Service",
    path: "/caseStudies/projects/bigJerrysFencing",
    featured: true,
    location: "United States",
    summary: "End-to-end digital transformation across 21 cities in 9 U.S. states.",
    metrics: [
      { value: "40%", label: "Faster turnaround from lead to completed installation" },
      { value: "25%", label: "Higher quote-to-win ratio with Good / Better / Best quoting options" },
    ],
    clientLogoName: "Big Jerry's Fencing",
    photoFile: "news/Bigjerryfencing.jpg",
  },
  {
    name: "Nurtureful Psychology",
    industry: "Healthcare",
    path: "/caseStudies/projects/nurturefulPsychology",
    summary: "End-to-end digital transformation for a premier mental health practice.",
    metrics: [
      { value: "50%", label: "Reduction in administrative time spent on scheduling and manual patient onboarding" },
    ],
    clientLogoName: "Nurtureful Psychology",
    photoFile: "caseStudies/nurturefulth.png",
  },
  {
    name: "Centric Events",
    industry: "Events & Production",
    path: "/caseStudies/projects/centricEvents",
    summary: "One connected system from first enquiry to final invoice for a Phoenix live events company.",
    metrics: [{ value: "100%", label: "Of change orders tracked and billed" }],
    clientLogoName: "Centric Events",
    photoFile: "caseStudies/centricEvents-hero.jpg",
  },
  {
    name: "EcoZohm",
    industry: "Manufacturing",
    path: "/caseStudies/projects/ecoZohm",
    summary: "Odoo ERP for a sustainable LED lighting manufacturer, live in seven weeks.",
    metrics: [{ value: "99%", label: "Quote accuracy through automation" }],
    clientLogoName: "EcoZohm",
    photoFile: "caseStudies/ecoZohm-hero.jpg",
  },
  {
    name: "Cakes & Bakes",
    industry: "Manufacturing",
    path: "/caseStudies/projects/cakesAndBakes",
    summary: "Dynamics 365 re-implemented for a production facility of Pakistan's first ISO-certified bakery chain.",
    metrics: [{ value: "100%", label: "Of issuance and dispatch in Dynamics 365" }],
    clientLogoName: "Cakes & Bakes",
    photoFile: "caseStudies/cakesAndBakes-hero.jpg",
  },
  {
    name: "Borjan",
    industry: "Retail",
    path: "/caseStudies/projects/borjan",
    summary: "Supply chain and finance reporting on Dynamics 365 for a 130+ outlet footwear retailer.",
    metrics: [{ value: "25+", label: "Supply chain and finance reports" }],
    clientLogoName: "Borjan",
    photoFile: "caseStudies/borjan-hero.jpg",
  },
  {
    name: "Hush Puppies Pakistan",
    industry: "Distribution",
    path: "/caseStudies/projects/hushPuppiesPakistan",
    summary: "Dynamics 365 support, integrations and retail changes for Firhaj Footwear.",
    metrics: [{ value: "In sync", label: "Orders, stock and prices with Xstak" }],
    // "Hush Puppies" (not "Hush Puppies Pakistan") in clientLogos.js.
    clientLogoName: "Hush Puppies",
    photoFile: "caseStudies/hushPuppiesPakistan-hero.jpg",
  },
  {
    name: "Net Atlantic",
    industry: "Software & SaaS",
    path: "/caseStudies/projects/netAtlantic",
    summary: "A rebuilt email marketing platform for an email service provider running since 1995.",
    metrics: [{ value: "35%", label: "Faster campaign deployment" }],
    clientLogoName: "Net Atlantic",
    photoFile: "caseStudies/netAtlantic-hero.jpg",
  },
  {
    name: "TTI Testing Laboratories",
    industry: "Testing & Inspection",
    path: "/caseStudies/projects/ttiTestingLaboratories",
    summary: "A lab system connected to Odoo for an international textile testing laboratory.",
    built: "Odoo · Lab integration · HR workflows",
    clientLogoName: null,
    photoFile: "caseStudies/ttiTestingLaboratories-hero.jpg",
  },
  {
    name: "Muscat Media Group",
    industry: "Media",
    path: "/caseStudies/projects/muscatMediaGroup",
    summary: "Automated HR management system for Oman's leading media conglomerate.",
    metrics: [
      { value: "100%", label: "HR workflow automated — complete migration from manual spreadsheets to a digital Odoo system" },
    ],
    clientLogoName: "Muscat Media Group",
    photoFile: "caseStudies/MuscatMediaGroupoman.jpg",
  },
  {
    name: "Orangeline Pakistan",
    industry: "Construction & Engineering",
    path: "/caseStudies/projects/orangelinePakistan",
    summary: "Centralised construction management from planning to final handover.",
    metrics: [{ value: "30%", label: "Faster project turnaround from planning to final handover" }],
    // "Orange Line" in clientLogos.js — same client, a different name than
    // this story's own (the construction/rail client is called "Orange
    // Line" in the shared logo list, "Orangeline Pakistan" here).
    clientLogoName: "Orange Line",
    photoFile: "caseStudies/orangelinetrain1.jpg",
  },
  {
    name: "EME Aquatic Systems",
    industry: "Construction & Engineering",
    path: "/caseStudies/projects/emeAquaticSystems",
    summary: "Procurement security and financial clarity for Abu Dhabi's leading aquatic infrastructure company.",
    // Briefed as a "built line" story (no metric) — its own page's first
    // result is actually a metric, so that's what's used here instead.
    metrics: [{ value: "3-Stage", label: "Procurement security with department-level data silos" }],
    clientLogoName: "EME Aquatic Systems",
    photoFile: "caseStudies/eme-story1.jpg",
  },
  {
    name: "ORR Protection",
    industry: "Field Service",
    path: "/caseStudies/projects/orrProtection",
    summary: "Odoo, a technician portal and a technician planner for a fire protection specialist founded in 1948.",
    built: "Odoo · Technician portal · Technician planner",
    clientLogoName: "ORR Protection",
    photoFile: "clients/orrProtection-card.jpg",
  },
  {
    name: "United Gulf Pipe Manufacturing",
    industry: "Manufacturing",
    path: "/caseStudies/projects/unitedGulfPipe",
    summary: "Odoo CRM, sales, purchase and manufacturing for a maker of HDPE pipes.",
    built: "Odoo · Master manufacturing order",
    clientLogoName: "United Gulf Pipe Manufacturing",
    photoFile: "clients/unitedGulfPipe-card.jpg",
  },
  {
    name: "Sohar Port & Freezone",
    industry: "Distribution",
    path: "/caseStudies/projects/soharPort",
    summary: "Container management on Odoo for Oman's deep-sea port and freezone.",
    built: "Odoo · Container management · Ministry reporting",
    clientLogoName: "Sohar Port & Freezone",
    photoFile: "clients/soharPort-card.jpg",
  },
  {
    name: "Huge Sports",
    industry: "Retail",
    path: "/caseStudies/projects/hugeSports",
    summary: "A full Odoo setup for a fitness equipment and sportswear group with over 50 years in the business.",
    built: "Odoo · Accounting · Inventory · HR",
    clientLogoName: "Huge Sports",
    photoFile: "clients/hugeSports-card.jpg",
  },
  {
    name: "Aamaal Group",
    industry: "Retail",
    path: "/caseStudies/projects/aamaalGroup",
    summary: "Odoo point of sale across the franchises of a Muscat franchising group.",
    built: "Odoo · Point of sale · Supply chain",
    clientLogoName: "Aamaal Group",
    photoFile: "clients/aamaalGroup-card.jpg",
  },
  {
    name: "United Al-Zahra",
    industry: "Retail",
    path: "/caseStudies/projects/unitedAlZahra",
    summary: "Odoo 10 to 13 migration for a multi-store hardware and household goods chain.",
    built: "Odoo · Version migration · Customisations",
    clientLogoName: "United Al-Zahra",
    photoFile: "clients/unitedAlZahra-card.jpg",
  },
]

// Chip order, fixed — counts are computed from STORIES at render time.
const INDUSTRIES = [
  "Manufacturing",
  "Retail",
  "Distribution",
  "Healthcare",
  "Field Service",
  "Events & Production",
  "Construction & Engineering",
  "Software & SaaS",
  "Testing & Inspection",
  "Media",
]

const PRODUCTS = [
  {
    name: "VisioEdge",
    description: "An Odoo-based ERP for retail, restaurants and complex warehousing",
    path: "/caseStudies/products/visioedge",
  },
  {
    name: "Viztos",
    description: "A point of sale for restaurants and retail that keeps working offline",
    path: "/caseStudies/products/viztos",
  },
  {
    name: "Distribution Utility",
    description: "Bulk stock transfers to many stores at once, outside Dynamics 365",
    path: "/caseStudies/products/mD365BulkStockDistribution",
  },
  {
    name: "Class Management",
    description: "Scheduling, attendance and grading for education and training teams",
    path: "/caseStudies/products/classManagement",
  },
  {
    name: "Donation Campaign Management",
    description: "Campaigns, donors and collections managed in one place",
    path: "/caseStudies/products/donationCampaignManagement",
  },
  {
    name: "360SafeOps",
    description: "Resident requests, maintenance and bookings in one app for managed residential buildings",
    externalUrl: "https://360safeops.com",
  },
]

module.exports = { STORIES, INDUSTRIES, PRODUCTS }
